import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { MdStarBorder, MdStar, MdMoreVert } from 'react-icons/md';
import Button from '../ui/Button';
import Skeleton from 'react-loading-skeleton';
import './styles.css';


function setCookie(name, value, days = 365) {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
}
function getCookie(name) {
  const regex = new RegExp('(^| )' + name + '=([^;]+)');
  const match = regex.exec(document.cookie);
  return match ? decodeURIComponent(match[2]) : null;
}

const ActionButtonWithMenu = ({
  options,
  defaultFavoriteId,
  className = '',
  loading = false
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({});
  const [hoveredStarId, setHoveredStarId] = useState(null);

  const cookieKey = useMemo(() => {
    const ids = options.map(o => o.id).join('_');
    return `actionMenuFavorite_${ids}`;
  }, [options]);

  const [favoriteId, setFavoriteId] = useState(() => {
    const cookieVal = getCookie(cookieKey);
    return cookieVal || defaultFavoriteId || null;
  });

  const mainAction = useMemo(() => (
    favoriteId
      ? options.find(option => option.id === favoriteId)
      : options[0]
  ), [options, favoriteId]);

  const dropdownOptions = useMemo(() => (
    options.filter(option => option.id !== favoriteId)
  ), [options, favoriteId]);

  const toggleFavorite = useCallback((optionId, e) => {
    e.stopPropagation();
    setFavoriteId(optionId);
    setCookie(cookieKey, optionId);
  }, [cookieKey]);

  const handleOptionClick = useCallback((option) => {
    option.action();
    setIsDropdownOpen(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // no off-screen positioning for dropdown menu
  useEffect(() => {
    if (isDropdownOpen && menuRef.current) {
      const menu = menuRef.current;
      const rect = menu.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      let style = {};
      if (rect.right > viewportWidth) {
        style.right = 0;
        style.left = 'auto';
      }
      if (rect.left < 0) {
        style.left = 0;
        style.right = 'auto';
      }
      if (rect.bottom > viewportHeight) {
        style.bottom = '100%';
        style.top = 'auto';
      }
      setMenuPosition(style);
    } else {
      setMenuPosition({});
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    if (!options.find(o => o.id === favoriteId)) {
      setFavoriteId(options[0]?.id || null);
    }
  }, [options, favoriteId]);

  if (loading) {
    return (
      <div className={`d-flex gap-2 ${className}`}>
        <Skeleton width={120} height={38} style={{ borderRadius: 5 }} />
        <Skeleton circle width={38} height={38} />
      </div>
    );
  }

  if (!options || options.length === 0) {
    return null;
  }

  return (
    <div className={`d-flex gap-2 ${className}`}>
      {mainAction && (
        <Button
          label={mainAction.label}
          onClick={() => handleOptionClick(mainAction)}
          icon={mainAction.icon}
        />
      )}

      {dropdownOptions.length > 0 && (
        <div className="dropdown" ref={dropdownRef}>
          <button
            className="action-menu-more-btn bg-transparent border-0"
            style={{ height: '100%' }}
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <MdMoreVert size={24} className="custom-theme-color" />
          </button>

          {isDropdownOpen && (
            <ul
              className="dropdown-menu p-2 show position-absolute mt-2 action-menu-dropdown-menu"
              ref={menuRef}
              style={{
                ...menuPosition
              }}
            >
              {dropdownOptions.map(option => (
                <li key={option.id}>
                  <div className="d-flex align-items-center justify-content-between gap-1">
                    <button
                      className={`d-flex align-items-center flex-grow-1 m-0 p-0 p-2 border-0 bg-transparent action-menu-dropdown-item text-nowrap ${option.isDestructive ? 'text-danger' : ''}`}
                      onClick={() => handleOptionClick(option)}
                    >
                      <span className={option.isDestructive ? 'text-danger d-flex align-items-center' : 'd-flex align-items-center'}>
                        {option.icon}
                      </span>
                      <span className={`px-2 ${option.isDestructive ? 'text-danger' : ''}`}>{option.label}</span>
                    </button>
                    <button
                      className="btn p-2 m-0 border-0 bg-transparent action-menu-star-btn action-menu-dropdown-item"
                      onClick={(e) => toggleFavorite(option.id, e)}
                      title="Set as main action"
                      onMouseEnter={() => setHoveredStarId(option.id)}
                      onMouseLeave={() => setHoveredStarId(null)}
                    >
                      {hoveredStarId === option.id ? (
                        <MdStar size={18} className="action-menu-star-icon" />
                      ) : (
                        <MdStarBorder size={18} color="var(--theme-text)"  />
                      )}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

ActionButtonWithMenu.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      action: PropTypes.func.isRequired,
      isDestructive: PropTypes.bool
    })
  ).isRequired,
  defaultFavoriteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  loading: PropTypes.bool
};

export default ActionButtonWithMenu;